import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Divider, Row, Col, Form, Input, DatePicker, notification, Space, Calendar } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import '../Atoms/Nominee.css';
import dayjs from 'dayjs';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';

const baseURL = process.env.REACT_APP_API_URL;

interface CreateCampaignModalProps {
    visible: boolean;
    onCancel: () => void;
    empData: any;
    isNewCampaign: boolean;
}

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({ visible, onCancel, empData, isNewCampaign }) => {
    const [newCampaignForm] = Form.useForm();
    const { instance, accounts } = useMsal();
    const activeCampaignData = empData?.responseData[0];
    const peopleManagerRemindersArray = activeCampaignData?.peopleManagerReminders?.split(',').map((date: any) => date.trim());
    const [reminderDates, setReminderDates] = useState<string[]>(peopleManagerRemindersArray || []);
    const [editingDate, setEditingDate] = useState<string | null>(null);

    useEffect(() => {
        if (!isNewCampaign && empData) {

            const formattedReminders = {
                managerReminders: activeCampaignData.peopleManagerReminders ? activeCampaignData.peopleManagerReminders.split(',').map((date: string) => dayjs(date.trim())) : [],
                firstManagerReminder: activeCampaignData.firstLevelManagerReminders ? activeCampaignData.firstLevelManagerReminders.split(',').map((date: string) => dayjs(date.trim())) : [],
                secondManagerReminder: activeCampaignData.secondManagerReminders ? activeCampaignData.secondManagerReminders.split(',').map((date: string) => dayjs(date.trim())) : [],
                stackReminder: activeCampaignData.stackReminders ? activeCampaignData.stackReminders.split(',').map((date: string) => dayjs(date.trim())) : [],
            }

            newCampaignForm.setFieldsValue({
                campaignName: activeCampaignData.campaignName || '',
                nominationStartDate: activeCampaignData.nominationStartDate ? dayjs(activeCampaignData.nominationStartDate) : null,
                nominationEndDate: activeCampaignData.nominationEndDate ? dayjs(activeCampaignData.nominationEndDate) : null,
                managerReviewStartDate: activeCampaignData.peopleManagerStartDate ? dayjs(activeCampaignData.peopleManagerStartDate) : null,
                managerReviewEndDate: activeCampaignData.peopleManagerEndDate ? dayjs(activeCampaignData.peopleManagerEndDate) : null,
                firstReviewStartDate: activeCampaignData.firstLevelManagerStartDate ? dayjs(activeCampaignData.firstLevelManagerStartDate) : null,
                firstReviewEndDate: activeCampaignData.firstLevelManagerEndDate ? dayjs(activeCampaignData.firstLevelManagerEndDate) : null,
                secondReviewStartDate: activeCampaignData.secondManagerStartDate ? dayjs(activeCampaignData.secondManagerStartDate) : null,
                secondReviewEndDate: activeCampaignData.secondManagerEndDate ? dayjs(activeCampaignData.secondManagerEndDate) : null,
                stackReviewStartDate: activeCampaignData.stackStartDate ? dayjs(activeCampaignData.stackStartDate) : null,
                stackReviewEndDate: activeCampaignData.stackEndDate ? dayjs(activeCampaignData.stackEndDate) : null,
                finalReviewStartDate: activeCampaignData.finalReviewStartDate ? dayjs(activeCampaignData.finalReviewStartDate) : null,
                finalReviewEndDate: activeCampaignData.finalReviewEndDate ? dayjs(activeCampaignData.finalReviewEndDate) : null,
                ...formattedReminders
            });
        }
    }, [isNewCampaign, empData, newCampaignForm]);

    const handleCancel = () => {
        onCancel();
    };

    const ReminderList = ({ name, label, reminders }: { name: string; label: string; reminders?: string[] }) => (
        <Form.List name={name}>
            {(fields, { add, remove }) => (
                <>
                    {
                        <>
                            {fields.map((field) => (
                                <Space key={field.key} style={{ display: 'flex', marginTop: '25px' }} align="baseline">
                                    <Form.Item
                                        {...field}
                                        validateTrigger={['onChange', 'onBlur']}
                                        rules={[
                                            {
                                                required: true,
                                                message: "Please select a reminder date or delete this field.",
                                            },
                                        ]}
                                    >
                                        <DatePicker style={{ width: '100%' }} />
                                    </Form.Item>
                                    {fields.length > 1 && (
                                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                                    )}
                                </Space>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add {label}
                                </Button>
                            </Form.Item>
                        </>
                    }
                </>
            )}
        </Form.List>
    );

    const handleNewCampaignSubmit = async (values: any) => {
        try {
            const formattedValues = {
                ...values,
                nominationStartDate: dayjs(values.nominationStartDate).format('YYYY-MM-DD'),
                nominationEndDate: dayjs(values.nominationEndDate).format('YYYY-MM-DD'),
                peopleManagerStartDate: dayjs(values.managerReviewStartDate).format('YYYY-MM-DD'),
                peopleManagerEndDate: dayjs(values.managerReviewEndDate).format('YYYY-MM-DD'),
                firstLevelManagerStartDate: dayjs(values.firstLevelManagerStartDate).format('YYYY-MM-DD'),
                firstLevelManagerEndDate: dayjs(values.firstLevelManagerEndDate).format('YYYY-MM-DD'),
                secondManagerStartDate: dayjs(values.secondReviewStartDate).format('YYYY-MM-DD'),
                secondManagerEndDate: dayjs(values.secondReviewEndDate).format('YYYY-MM-DD'),
                stackStartDate: dayjs(values.stackReviewStartDate).format('YYYY-MM-DD'),
                stackEndDate: dayjs(values.stackReviewEndDate).format('YYYY-MM-DD'),
                finalReviewStartDate: dayjs(values.finalReviewStartDate).format('YYYY-MM-DD'),
                finalReviewEndDate: dayjs(values.finalReviewEndDate).format('YYYY-MM-DD'),
                peopleManagerReminders: (values.managerReminders.map((date: any) => dayjs(date).format('YYYY-MM-DD'))).join(', '),
                firstLevelManagerReminders: (values.firstManagerReminder.map((date: any) => dayjs(date).format('YYYY-MM-DD'))).join(', '),
                secondManagerReminders: (values.secondManagerReminder.map((date: any) => dayjs(date).format('YYYY-MM-DD'))).join(', '),
                stackReminders: (values.stackReminder.map((date: any) => dayjs(date).format('YYYY-MM-DD'))).join(', '),
                campaignStatus: 'active'
            };

            const res = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            });

            const response = await axios.post(`${baseURL}/api/nominations/create/campaigninfo`, formattedValues, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${res.idToken}`,
                },
            });
            notification.success({
                message: 'New Campaign Created',
                description: 'New campaign has been created successfully.',
            });
            handleCancel();
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <Modal
            title="Create New Campaign"
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
            ]}
            width={1000}
        >
            <Form form={newCampaignForm} onFinish={handleNewCampaignSubmit} layout='vertical'>
                <Form.Item
                    name="campaignName"
                    label="Campaign Name"
                    rules={[{ required: true, message: 'Please enter the campaign name' }]}
                >
                    <Input />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="nominationStartDate"
                            label="Nomination Start Date"
                            rules={[{ required: true, message: 'Please select the nomination start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} disabledDate={(current) => {
                                return current && current < dayjs().startOf('day');
                            }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="nominationEndDate"
                            label="Nomination End Date"
                            rules={[{ required: true, message: 'Please select the nomination end date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider />

                {/* People's Manager */}

                <Row gutter={16}>
                    <Col span={8}>

                        <Form.Item
                            name="managerReviewStartDate"
                            label="People's Manager Review Start Date"
                            rules={[{ required: true, message: 'Please select the manager review start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="managerReviewEndDate"
                            label="People's Manager Review End Date"
                            rules={[{ required: true, message: 'Please select the manager review end date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <ReminderList name="managerReminders" label="Reminder for People's Manager" />
                    </Col>
                </Row>

                {/* First Level Manager */}
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="firstReviewStartDate"
                            label="First Level Manager Review Start Date"
                            rules={[{ required: true, message: 'Please select the manager review start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="firstReviewEndDate"
                            label="First Level Manager Review End Date"
                            rules={[{ required: true, message: 'Please select the manager review end date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <ReminderList name="firstManagerReminder" label="Reminder for First Level Manager" />
                    </Col>
                </Row>


                {/* Second Level Manager */}
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="secondReviewStartDate"
                            label="Second Level Manager Review Start Date"
                            rules={[{ required: true, message: "Please select the second level reviewer's start date" }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="secondReviewEndDate"
                            label="Second Level Manager Review End Date"
                            rules={[{ required: true, message: "Please select the second level reviewer's End date" }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <ReminderList name="secondManagerReminder" label="Reminder for Second Level Manager" />
                    </Col>
                </Row>

                {/* Stack Ranking */}
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="stackReviewStartDate"
                            label="Stack Ranking Start Date"
                            rules={[{ required: true, message: "Please select start date" }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="stackReviewEndDate"
                            label="Stack Ranking End Date"
                            rules={[{ required: true, message: "Please select End date" }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <ReminderList name="stackReminder" label="Reminder for Stack Ranking" />
                    </Col>
                </Row>
                <Divider orientation='center'>Final Review</Divider>

                {/* Final Review */}
                <Row gutter={16}>
                    <Col span={12}>

                        <Form.Item
                            name="finalReviewStartDate"
                            label="Final Review Start Date"
                            rules={[{ required: true, message: 'Please select the start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="finalReviewEndDate"
                            label="Final Review End Date"
                            rules={[{ required: true, message: 'Please select the end date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        {isNewCampaign ? "Create Campaign" : "Update Campaign"}
                    </Button>
                </Form.Item>
            </Form>

            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Manager Review..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Unit Head Review..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for ELT Review..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Stack Ranking..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Manager Reminder..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Unit Head Reminder..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for ELT Reminder..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
            <Input style={{ marginTop: '10px', marginLeft: '10px' }} placeholder="Enter the Template for Stack Reminder..." />
            <Button type="primary" style={{ marginTop: '10px', marginLeft: '10px' }}>Preview Template</Button>
        </Modal>
    );
};

export default CreateCampaignModal;
